/* eslint-disable max-lines -- Pre-existing violation */
/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {
  Card,
  DataStatus,
  Dialog,
  Form,
  FormButton,
  FormField,
  Separator,
  type FormControl,
  type FormSubmitHandler,
  Table,
  TableRow,
  closeDialog,
  useFormContext,
  openConfirmDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';
import {useWatch} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  newVehicleApi,
  useArchiveEngineMutation,
  useArchiveTrimMutation,
  useCreateEngineMutation,
  useCreateTrimMutation,
  useListEngineQuery,
  useListTrimQuery,
  useUpdateEngineMutation,
  useUpdateTrimMutation,
  type EngineResponseBody,
  type PricelistVersionRequestBody,
  type TrimResponseBody,
  type TrimWriteRequestBody,
} from '@dms/api/new-vehicle';
import {
  MoneyText,
  NewVehicleStatusFlag,
  OrderabilityChip,
  getNewVehicleEnumLabel,
  useNewVehicleCatalogueLabels,
  refetchOnImageExpiry,
} from '@dms/features/new-vehicle';
import {
  DriveSelect,
  FuelTypeSelect,
  TransmissionSelect,
  useVehicleCatalogue,
  VehicleStyleSelect,
} from '@dms/shared';

import {getRandomId, type ApiError} from 'shared/web';

import {useCatalogueVatRate} from '../hooks/useCatalogueVatRate';
import {useCopyFromTrim} from '../hooks/useCopyFromTrim';
import {usePublishedPriceSave} from '../hooks/usePublishedPriceSave';
import {useTrimPublicationChanges} from '../hooks/useTrimPublicationChanges';
import {engineAuthoringSchema, type EngineAuthoringValues} from '../schemas/engineSchema';
import {
  toEngineTechnicalData,
  toEngineTechnicalDataValues,
} from '../schemas/engineTechnicalDataSchema';
import {trimSchema} from '../schemas/trimSchema';
import type {PricedItemChange} from '../types/PricedItemChange';
import {ITEM_CHANGE_CANCELLED} from '../types/PricedItemChangeOutcome';
import {
  actionsColumn,
  archiveRowAction,
  Cell,
  column,
  editRowAction,
  engineHybridType,
  handleCatalogueError,
  isStaleCatalogueVersion,
  HYBRID_FUEL,
  optionalText,
  PASSENGER_CAR,
  FormSection,
  RowActions,
  TabIntro,
  t,
} from '../utils/catalogueUi';
import {createdItem} from '../utils/createdItem';
import {formatBatteryCapacity} from '../utils/formatConsumptionAndEmission';
import {getTechnicalDataFit} from '../utils/getTechnicalDataFit';
import {hasAnyImage} from '../utils/hasAnyImage';
import {isInPricelist} from '../utils/isInPricelist';
import {newerRecord} from '../utils/newerRecord';
import {pricedItemGroupKeys} from '../utils/pricedItemGroupKeys';
import {removedItem} from '../utils/removedItem';
import {statusWithPriceError} from '../utils/statusWithPriceError';
import {trimWindow} from '../utils/trimWindow';
import {unchangedStoredVersion} from '../utils/unchangedStoredVersion';
import {CatalogueImageField} from './CatalogueImageField';
import {CatalogueThumbnail} from './CatalogueThumbnail';
import {EngineBatteryFields} from './EngineBatteryFields';
import {GrossPriceField} from './GrossPriceField';
import {NegativeMarginWarning} from './NegativeMarginWarning';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';
import {NumberField} from './NumberField';
import {PriceRow} from './PriceRow';
import {PriceWithGross} from './PriceWithGross';

const trimAuthoringSchema = trimSchema
  .extend({
    publicationStatus: z.enum(['draft', 'online', 'archived']),
    commercialAvailability: z.enum(['orderable', 'preview', 'sold_out']),
    copyFromTrimId: optionalText,
  })
  .superRefine(trimWindow)
  // Mirrors the backend rule (DMS-NV-CT-0025): an online trim needs a type.
  .superRefine((values, ctx) => {
    if (values.publicationStatus === 'online' && !values.type.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['type' satisfies keyof typeof values],
        message: t(
          'entity.newVehicle.catalogue.validation.trimTypeRequiredOnline',
          'Enter a type to publish the trim.'
        ),
      });
    }
  });
type TrimValues = z.infer<typeof trimAuthoringSchema>;
type EngineValues = EngineAuthoringValues;

type TrimFieldsProps = {
  control: FormControl<TrimValues>;
  modelYearId: string;
  canViewCost: boolean;
  currency: string;
  isCreate?: boolean;
  copyFromTrims?: TrimResponseBody[];
  submitTitle: string;
};

function TrimFields(props: TrimFieldsProps) {
  const form = useFormContext<TrimValues>();
  const vatRate = useCatalogueVatRate(props.modelYearId);
  const publicationStatus = useWatch({control: props.control, name: 'publicationStatus'});
  // The type rule spans type and status, so re-check the type when either changes.
  const revalidateType = () => void form.trigger('type');
  return (
    <VStack spacing={4}>
      <FormSection title={t('entity.newVehicle.catalogue.section.identity', 'Identity')}>
        <FormField
          control={props.control}
          name="name"
          type="text"
          label={t('general.labels.name', 'Name')}
          placeholder="Titanium"
          isRequired={props.isCreate}
        />
        <Grid columns={2} spacing={3}>
          <VehicleStyleSelect
            vehicleType={PASSENGER_CAR}
            control={props.control}
            name="bodyStyle"
            label={t('entity.newVehicle.catalogue.bodyStyle', 'Body style')}
            isMenuInPortal
            isNotClearable
            isRequired
            data-testid="newVehicleCatalogue-trim"
          />
          <FormField
            control={props.control}
            name="type"
            type="text"
            label={t('entity.newVehicle.catalogue.type', 'Type')}
            isRequired={publicationStatus === 'online'}
            onChange={revalidateType}
            helperText={t(
              'entity.newVehicle.catalogue.trimTypePublishHelp',
              'Marketing line shown under the name, e.g. "Sport". Required to publish the trim.'
            )}
          />
        </Grid>
      </FormSection>
      <Separator spacing={0} />
      <FormSection title={t('entity.newVehicle.catalogue.section.selling', 'Selling')}>
        <Grid columns={2} spacing={3}>
          <FormField
            control={props.control}
            name="publicationStatus"
            type="choice"
            menuInPortal
            isNotClearable
            label={t('entity.newVehicle.catalogue.publication', 'Publication')}
            onChange={revalidateType}
            helperText={t(
              'entity.newVehicle.catalogue.publicationHelp',
              'Draft trims stay hidden from the configurator.'
            )}
            options={['draft', 'online'].map((value) => ({
              value,
              label: getNewVehicleEnumLabel('publicationStatus', value, t),
            }))}
          />
          <FormField
            control={props.control}
            name="commercialAvailability"
            type="choice"
            menuInPortal
            isNotClearable
            label={t('entity.newVehicle.catalogue.commercialAvailability', 'Availability')}
            helperText={t(
              'entity.newVehicle.catalogue.availabilityHelp',
              'Preview shows the trim without taking orders.'
            )}
            options={['orderable', 'preview', 'sold_out'].map((value) => ({
              value,
              label: getNewVehicleEnumLabel('commercialAvailability', value, t),
            }))}
          />
          <FormField
            control={props.control}
            name="validFrom"
            type="apiDate"
            label={t('entity.newVehicle.catalogue.validFrom', 'Valid from')}
          />
          <FormField
            control={props.control}
            name="validTo"
            type="apiDate"
            label={t('entity.newVehicle.catalogue.validTo', 'Valid to')}
          />
        </Grid>
      </FormSection>
      <Separator spacing={0} />
      <FormSection title={t('entity.newVehicle.catalogue.section.price', 'Price')}>
        <PriceRow vatRate={vatRate} canViewCost={props.canViewCost} spacing={3}>
          <GrossPriceField
            control={props.control}
            name="basePriceNet"
            vatRate={vatRate}
            label={t('entity.newVehicle.catalogue.basePriceNet', 'Base price net')}
            helperText={t(
              'entity.newVehicle.catalogue.basePriceHelp',
              'Starting price before the engine is chosen.'
            )}
            isRequired={props.isCreate}
          />
          <Show when={props.canViewCost}>
            <NumberField
              control={props.control}
              name="costNet"
              label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
            />
          </Show>
        </PriceRow>
        <NegativeMarginWarning
          control={props.control}
          priceName="basePriceNet"
          costName="costNet"
          currency={props.currency}
          canViewCost={props.canViewCost}
        />
      </FormSection>
      <Show when={props.copyFromTrims?.length ? props.copyFromTrims : null}>
        {(trims) => (
          <>
            <Separator spacing={0} />
            <FormField
              control={props.control}
              name="copyFromTrimId"
              type="choice"
              menuInPortal
              label={t('entity.newVehicle.catalogue.copyFromTrim', 'Copy from trim')}
              helperText={t(
                'entity.newVehicle.catalogue.copyFromTrimHelp',
                'Starts the new trim with the equipment, colours and packages of this trim, at its prices. Items offered only with some of its variants are left out.'
              )}
              options={trims.map(({id, name}) => ({value: id, label: name}))}
            />
          </>
        )}
      </Show>
      <HStack justify="flex-end">
        <FormButton control={props.control} type="submit" title={props.submitTitle} />
      </HStack>
    </VStack>
  );
}

type EngineFieldsProps = {
  control: FormControl<EngineValues>;
  isCreate?: boolean;
  submitTitle: string;
};

function EngineFields(props: EngineFieldsProps) {
  const form = useFormContext<EngineValues>();
  const fuel = useWatch({control: props.control, name: 'fuel'});
  const [catalogue, {isLoading}] = useVehicleCatalogue(PASSENGER_CAR);
  const hasBattery = getTechnicalDataFit({fuel}).hasBattery;
  const clearMisfittingValues = (value: string | number | string[] | null) => {
    if (value !== HYBRID_FUEL) {
      form.setValue('hybridType', '');
    }
    if (!getTechnicalDataFit({fuel: typeof value === 'string' ? value : ''}).hasBattery) {
      form.setValue('technicalData', {
        ...toEngineTechnicalDataValues(undefined),
        hasDpf: form.getValues('technicalData.hasDpf'),
      });
    }
  };
  return (
    <VStack spacing={4}>
      <FormSection title={t('entity.newVehicle.catalogue.section.identity', 'Identity')}>
        <Grid columns={2} spacing={3}>
          <FormField
            control={props.control}
            name="name"
            type="text"
            label={t('general.labels.name', 'Name')}
            placeholder="1.5 EcoBoost 110kW"
            isRequired={props.isCreate}
          />
          <FormField
            control={props.control}
            name="factoryCode"
            type="text"
            label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
          />
        </Grid>
      </FormSection>
      <Separator spacing={0} />
      <FormSection title={t('entity.newVehicle.catalogue.section.powertrain', 'Powertrain')}>
        <Grid columns={2} spacing={3}>
          <FuelTypeSelect
            vehicleType={PASSENGER_CAR}
            control={props.control}
            name="fuel"
            label={t('entity.newVehicle.catalogue.fuel', 'Fuel')}
            isMenuInPortal
            isNotClearable
            isRequired
            onChange={clearMisfittingValues}
            data-testid="newVehicleCatalogue-engine"
          />
          <Show when={fuel === HYBRID_FUEL}>
            <FormField
              control={props.control}
              name="hybridType"
              type="choice"
              menuInPortal
              label={t('general.labels.hybridType', 'Hybrid type')}
              options={catalogue.hybridTypeOptions ?? []}
              isLoading={isLoading}
              isDisabled={isNilOrEmpty(catalogue.hybridTypeOptions)}
            />
          </Show>
          <TransmissionSelect
            vehicleType={PASSENGER_CAR}
            control={props.control}
            name="transmission"
            label={t('entity.newVehicle.catalogue.transmission', 'Transmission')}
            isMenuInPortal
            isNotClearable
            isRequired
            data-testid="newVehicleCatalogue-engine"
          />
          <DriveSelect
            vehicleType={PASSENGER_CAR}
            control={props.control}
            name="drive"
            label={t('entity.newVehicle.catalogue.drive', 'Drive')}
            isMenuInPortal
            isNotClearable
            isRequired
            data-testid="newVehicleCatalogue-engine"
          />
          <NumberField
            control={props.control}
            name="displacementCcm"
            label={`${t('entity.newVehicle.catalogue.displacement', 'Displacement')} (cm³)`}
          />
          <NumberField
            control={props.control}
            name="powerKw"
            label={t('entity.newVehicle.labels.kw', 'kW')}
            isRequired
          />
          <NumberField
            control={props.control}
            name="powerPs"
            label={t('entity.newVehicle.labels.ps', 'PS')}
          />
        </Grid>
        <FormField
          control={props.control}
          name="technicalData.hasDpf"
          type="checkbox"
          label={t('entity.vehicle.labels.particulateFilter')}
        />
      </FormSection>
      <Show when={hasBattery}>
        <Separator spacing={0} />
        <EngineBatteryFields control={props.control} />
      </Show>
      <HStack justify="flex-end">
        <FormButton control={props.control} type="submit" title={props.submitTitle} />
      </HStack>
    </VStack>
  );
}

/**
 * The stored trim when the write switches its publication status, as the BE compares it: the new
 * status differs from the stored one, and neither is archived (`trimSwitchWithNewItemRow`).
 */
const switchedTrimOf = (
  stored: Pick<TrimResponseBody, 'id' | 'name' | 'publicationStatus'>,
  next: TrimResponseBody['publicationStatus']
) =>
  stored.publicationStatus !== next &&
  stored.publicationStatus !== 'archived' &&
  next !== 'archived'
    ? {id: stored.id, name: stored.name}
    : undefined;

function TrimEditDialog({
  trim,
  canViewCost,
  currency,
  reload,
  dialogId,
}: {
  trim: TrimResponseBody;
  canViewCost: boolean;
  currency: string;
  reload: () => Promise<void>;
  dialogId: string;
}) {
  const [updateTrim] = useUpdateTrimMutation();
  const [getTrim] = newVehicleApi.useLazyGetTrimQuery();
  const publishedPrice = usePublishedPriceSave(trim.modelYearId);
  const readPublicationChanges = useTrimPublicationChanges();
  const [version, setVersion] = useState(trim.version);
  const trimList = useListTrimQuery({});
  const imageRecord = newerRecord(trim, trimList.data?.items);
  const defaultValues: TrimValues = {
    name: trim.name,
    bodyStyle: trim.bodyStyle,
    type: trim.type ?? '',
    publicationStatus: trim.publicationStatus,
    commercialAvailability: trim.commercialAvailability,
    validFrom: trim.orderableFrom ?? null,
    validTo: trim.orderableUntil ?? null,
    basePriceNet: trim.basePriceNet,
    costNet: trim.costNet ?? '',
    copyFromTrimId: '',
  };
  const toUpdateRequest = (values: TrimValues) => ({
    trimId: trim.id,
    trimWriteRequestBody: {
      modelYearId: trim.modelYearId,
      name: values.name,
      codes: trim.codes,
      bodyStyle: values.bodyStyle,
      type: values.type || null,
      basePriceNet: values.basePriceNet,
      costNet: canViewCost ? values.costNet || null : trim.costNet,
      publicationStatus: values.publicationStatus,
      commercialAvailability: values.commercialAvailability,
      orderableFrom: values.validFrom || null,
      orderableUntil: values.validTo || null,
      version,
    },
  });
  const reloadAndClose = async () => {
    await reload();
    closeDialog(dialogId);
  };
  /**
   * A switch between Draft and Online puts the trim's prices into the dealer price list or takes
   * them out (D88): a published year asks for a new pricelist version first. An unpublish with a
   * changed base price is refused by the server (P2), so it is refused here and nothing is sent.
   */
  const switchPublication = async (
    values: TrimValues,
    direction: 'publish' | 'unpublish',
    setErrors: Parameters<FormSubmitHandler<TrimValues>>[1]
  ) => {
    const isPublished = await publishedPrice.readIsPublished();
    if (
      isPublished &&
      direction === 'unpublish' &&
      Number(values.basePriceNet) !== Number(trim.basePriceNet)
    ) {
      setErrors([statusWithPriceError('basePriceNet')]);
      return;
    }
    // A publish lists the prices the write sends; an unpublish takes out the stored ones.
    const readItems = () =>
      readPublicationChanges(
        direction === 'publish'
          ? {...trim, name: values.name, basePriceNet: values.basePriceNet}
          : trim,
        direction
      );
    const outcome = await publishedPrice.writePricedItemChange({
      items: isPublished ? await readItems() : [],
      entry: {
        key: pricedItemGroupKeys.trim(trim.id),
        title: values.name,
        trimRecordId: trim.id,
        switchedTrim: switchedTrimOf(trim, values.publicationStatus),
        // The body is the editor's: a write to the trim after it was added fails the entry.
        build: async () => {
          const [items, storedVersion] = await Promise.all([
            readItems(),
            unchangedStoredVersion(version, () =>
              getTrim({trimId: trim.id}, false)
                .unwrap()
                .then((stored) => stored.version)
            ),
          ]);
          return {
            items,
            operations: [
              {
                type: 'trim.update',
                targetId: trim.id,
                body: {...toUpdateRequest(values).trimWriteRequestBody, version: storedVersion},
              },
            ],
          };
        },
      },
      write: (pricelistVersion) => {
        const request = toUpdateRequest(values);
        return updateTrim({
          ...request,
          trimWriteRequestBody: {
            ...request.trimWriteRequestBody,
            ...(pricelistVersion && {pricelistVersion}),
          },
        }).unwrap();
      },
    });
    // A cancelled pricelist version keeps the form open with its values.
    if (outcome !== ITEM_CHANGE_CANCELLED) {
      closeDialog(dialogId);
    }
  };
  const submit: FormSubmitHandler<TrimValues> = async (values, setErrors) => {
    const direction = match([
      isInPricelist.trim(trim.publicationStatus),
      isInPricelist.trim(values.publicationStatus),
    ])
      .with([false, true], () => 'publish' as const)
      .with([true, false], () => 'unpublish' as const)
      .otherwise(() => undefined);
    const request: Promise<unknown> = isNil(direction)
      ? publishedPrice.save({
          values,
          storedValues: defaultValues,
          priceField: 'basePriceNet',
          scope: 'trim',
          resourceId: trim.id,
          // The stored status decides, as on the server: a Draft trim is outside the price list.
          isDraftItem: !isInPricelist.trim(trim.publicationStatus),
          save: (trimValues) => updateTrim(toUpdateRequest(trimValues)).unwrap(),
          onDone: () => closeDialog(dialogId),
        })
      : switchPublication(values, direction, setErrors);
    await request.catch(async (error: ApiError) => {
      handleCatalogueError(error, setErrors);
      if (isStaleCatalogueVersion(error)) {
        await reloadAndClose();
      }
    });
  };
  return (
    <VStack spacing={4}>
      <FormSection title={t('entity.newVehicle.catalogue.image', 'Image')}>
        <CatalogueImageField
          owner="trim"
          ownerId={trim.id}
          image={imageRecord.imageAssetRef}
          ownerVersion={imageRecord.version}
          version={version}
          onVersionChange={setVersion}
          onStale={() => void reloadAndClose()}
          onImageError={refetchOnImageExpiry(trimList)}
        />
      </FormSection>
      <Separator spacing={0} />
      <Form<TrimValues>
        experimentalZodSchema={trimAuthoringSchema}
        defaultValues={defaultValues}
        onSubmit={submit}
      >
        {(control) => (
          <TrimFields
            control={control}
            modelYearId={trim.modelYearId}
            canViewCost={canViewCost}
            currency={currency}
            submitTitle={t('general.actions.save', 'Save')}
          />
        )}
      </Form>
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
    </VStack>
  );
}

function EngineEditDialog({
  engine,
  reload,
  dialogId,
}: {
  engine: EngineResponseBody;
  reload: () => Promise<void>;
  dialogId: string;
}) {
  const [updateEngine] = useUpdateEngineMutation();
  const submit: FormSubmitHandler<EngineValues> = async (values, setErrors) => {
    await updateEngine({
      engineId: engine.id,
      engineWriteRequestBody: {
        modelYearId: engine.modelYearId,
        name: values.name,
        codes: engine.codes,
        powerKw: values.powerKw,
        powerPs: values.powerPs || null,
        displacementCcm: values.displacementCcm ? +values.displacementCcm : null,
        factoryCode: values.factoryCode || null,
        transmissionCode: engine.transmissionCode ?? null,
        fuel: values.fuel,
        hybridType: engineHybridType(values),
        transmission: values.transmission,
        drive: values.drive,
        technicalData: toEngineTechnicalData(values.technicalData, values),
        version: engine.version,
      },
    })
      .unwrap()
      .then(() => closeDialog(dialogId))
      .catch(async (error: ApiError) => {
        handleCatalogueError(error, setErrors);
        if (isStaleCatalogueVersion(error)) {
          await reload();
          closeDialog(dialogId);
        }
      });
  };
  return (
    <Form<EngineValues>
      experimentalZodSchema={engineAuthoringSchema}
      defaultValues={{
        name: engine.name,
        fuel: engine.fuel,
        hybridType: engine.hybridType ?? '',
        powerKw: engine.powerKw,
        powerPs: engine.powerPs ?? '',
        displacementCcm: engine.displacementCcm?.toString() ?? '',
        transmission: engine.transmission,
        drive: engine.drive,
        factoryCode: engine.factoryCode ?? '',
        technicalData: toEngineTechnicalDataValues(engine.technicalData),
      }}
      onSubmit={submit}
    >
      {(control) => (
        <EngineFields control={control} submitTitle={t('general.actions.save', 'Save')} />
      )}
    </Form>
  );
}

/**
 * A trim create that joins an open group, with its client id. The Variant matrix lists the trim as a
 * new row, and a new cell of that row names the trim by its client id (F1 addendum 8.4).
 */
const groupedTrimCreate = (body: TrimWriteRequestBody, items: PricedItemChange[]) => {
  const grouped = pricedItemGroupKeys.newCreate('trim');
  return {
    ...grouped,
    title: body.name,
    createdTrim: {
      id: grouped.createdId,
      name: body.name,
      publicationStatus: body.publicationStatus,
    },
    build: () =>
      Promise.resolve({
        items,
        operations: [{type: 'trim.create' as const, id: grouped.createdId, body}],
      }),
  };
};

/** A trim archive that joins an open group: it sends the version read at Confirm. */
const trimArchiveEntry = (
  row: TrimResponseBody,
  readTrim: (trimId: string) => Promise<TrimResponseBody>
) => ({
  key: pricedItemGroupKeys.trim(row.id),
  title: row.name,
  trimRecordId: row.id,
  build: () =>
    readTrim(row.id).then((stored) => ({
      items: [removedItem('trim', row.id, row.name, row.basePriceNet)],
      operations: [
        {type: 'trim.archive' as const, targetId: row.id, body: {version: stored.version}},
      ],
    })),
});

export function TrimLevels({
  modelYearId,
  currency,
  canWrite,
  canViewCost,
}: {
  modelYearId: string;
  currency: string;
  canWrite: boolean;
  canViewCost: boolean;
}) {
  const query = useListTrimQuery({});
  const vatRate = useCatalogueVatRate(modelYearId);
  const [createTrim] = useCreateTrimMutation();
  const [getTrim] = newVehicleApi.useLazyGetTrimQuery();
  const [updateTrim] = useUpdateTrimMutation();
  const copyFromTrim = useCopyFromTrim(canViewCost);
  const [archiveTrim] = useArchiveTrimMutation();
  const publishedPrice = usePublishedPriceSave(modelYearId);
  const readPublicationChanges = useTrimPublicationChanges();
  const [isCreateOpen, setCreateOpen] = useState(false);
  const {getBodyStyle} = useNewVehicleCatalogueLabels();
  const rows =
    query.data?.items.filter((row) => row.modelYearId === modelYearId && !row.archived) ?? [];
  const createBody = (
    values: TrimValues,
    publicationStatus: TrimValues['publicationStatus']
  ): TrimWriteRequestBody => ({
    modelYearId,
    name: values.name,
    codes: [],
    bodyStyle: values.bodyStyle,
    type: values.type || null,
    basePriceNet: values.basePriceNet,
    costNet: values.costNet || null,
    publicationStatus,
    commercialAvailability: values.commercialAvailability,
    orderableFrom: values.validFrom || null,
    orderableUntil: values.validTo || null,
    version: 1,
  });
  const showImage = hasAnyImage(rows);
  const create = (
    values: TrimValues,
    publicationStatus: TrimValues['publicationStatus'],
    pricelistVersion?: PricelistVersionRequestBody
  ) =>
    createTrim({
      trimWriteRequestBody: {
        ...createBody(values, publicationStatus),
        ...(pricelistVersion && {pricelistVersion}),
      },
    }).unwrap();
  /**
   * Puts a Draft trim with its copied rows online: one pricelist version lists the base price and every
   * copied feature, colour and active package row (D88, D102), as the trim's own publish does.
   * Resolves how the trim was left: online, Draft after a cancelled version, or Draft with its publish
   * in the open group. A failed version rejects and leaves it Draft.
   */
  const publishCopiedTrim = async (trimId: string) => {
    const stored = await getTrim({trimId}, false).unwrap();
    const items = await readPublicationChanges(stored, 'publish');
    const publishBody: TrimWriteRequestBody = {
      modelYearId: stored.modelYearId,
      name: stored.name,
      codes: stored.codes,
      bodyStyle: stored.bodyStyle,
      type: stored.type ?? null,
      basePriceNet: stored.basePriceNet,
      costNet: stored.costNet ?? null,
      publicationStatus: 'online',
      commercialAvailability: stored.commercialAvailability,
      orderableFrom: stored.orderableFrom ?? null,
      orderableUntil: stored.orderableUntil ?? null,
      version: stored.version,
    };
    const outcome = await publishedPrice.writePricedItemChange({
      items,
      // With an open group the publish joins it; the Draft trim and its copies are stored already.
      entry: {
        key: pricedItemGroupKeys.trim(trimId),
        title: stored.name,
        trimRecordId: trimId,
        switchedTrim: switchedTrimOf(stored, publishBody.publicationStatus),
        build: async () => {
          const [publishItems, version] = await Promise.all([
            getTrim({trimId}, false)
              .unwrap()
              .then((current) => readPublicationChanges(current, 'publish')),
            unchangedStoredVersion(stored.version, () =>
              getTrim({trimId}, false)
                .unwrap()
                .then((current) => current.version)
            ),
          ]);
          return {
            items: publishItems,
            operations: [{type: 'trim.update', targetId: trimId, body: {...publishBody, version}}],
          };
        },
      },
      write: (pricelistVersion) =>
        updateTrim({
          trimId,
          trimWriteRequestBody: {...publishBody, ...(pricelistVersion && {pricelistVersion})},
        }).unwrap(),
    });
    return match(outcome)
      .with('grouped', () => 'grouped' as const)
      .with(ITEM_CHANGE_CANCELLED, () => 'draft' as const)
      .otherwise(() => 'online' as const);
  };
  /**
   * Copies onto an online trim of a published year would each need a pricelist version (D102): the
   * trim is created as Draft (no version), gets the copies (exempt), and then goes online with one
   * version. A copy that failed leaves it Draft, so no version lists a half-copied trim.
   */
  const createCopiedOnlineTrim = async (values: TrimValues, source: TrimResponseBody) => {
    const created = await create(values, 'draft');
    setCreateOpen(false);
    const {notCopied, isComplete} = await copyFromTrim(source.id, created);
    const publication = isComplete
      ? await publishCopiedTrim(created.id).catch((error: unknown) => {
          handleCatalogueError(error);
          return 'draft' as const;
        })
      : 'draft';
    reportCopy(source.name, notCopied, publication);
  };
  const submit: FormSubmitHandler<TrimValues> = async (values, setErrors) => {
    let created: TrimResponseBody | undefined;
    const source = rows.find(({id}) => id === values.copyFromTrimId);
    const isOnline = isInPricelist.trim(values.publicationStatus);
    try {
      if (isNotNil(source) && isOnline && (await publishedPrice.readIsPublished())) {
        return await createCopiedOnlineTrim(values, source);
      }
      // A Draft trim is outside the price list and needs no pricelist version.
      const items = isOnline
        ? [createdItem('trim', 'new-trim', values.name, values.basePriceNet)]
        : [];
      const outcome = await publishedPrice.writePricedItemChange({
        items,
        entry: groupedTrimCreate(createBody(values, values.publicationStatus), items),
        write: (pricelistVersion) =>
          create(values, values.publicationStatus, pricelistVersion).then((trim) => {
            created = trim;
          }),
      });
      // A cancelled pricelist version keeps the form open with its values.
      if (outcome === ITEM_CHANGE_CANCELLED) {
        return;
      }
      setCreateOpen(false);
      if (source && created) {
        const {notCopied} = await copyFromTrim(source.id, created);
        reportCopy(source.name, notCopied, 'online');
      }
    } catch (error) {
      handleCatalogueError(error, setErrors);
    }
  };
  const editTrim = (row: TrimResponseBody) => {
    const dialogId = `trim-edit-${row.id}-${getRandomId()}`;
    openDialog(
      <TrimEditDialog
        trim={row}
        canViewCost={canViewCost}
        currency={currency}
        dialogId={dialogId}
        reload={async () => {
          await query.refetch();
        }}
      />,
      {
        id: dialogId,
        title: t('entity.newVehicle.catalogue.editTrimNamed', 'Edit trim · {name}', {
          name: row.name,
        }),
      }
    );
  };
  const archive = (row: TrimResponseBody) =>
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archiveTrim', 'Archive trim level {name}?', {
        name: row.name,
      }),
      onConfirm: () =>
        void publishedPrice
          .writePricedItemChange({
            items: isInPricelist.trim(row.publicationStatus)
              ? [removedItem('trim', row.id, row.name, row.basePriceNet)]
              : [],
            entry: trimArchiveEntry(row, (trimId) => getTrim({trimId}, false).unwrap()),
            write: (pricelistVersion) =>
              archiveTrim({
                trimId: row.id,
                pricedItemArchiveRequestBody: {
                  version: row.version,
                  ...(pricelistVersion && {pricelistVersion}),
                },
              }).unwrap(),
          })
          .catch(handleCatalogueError),
    });
  return (
    <VStack spacing={3}>
      <TabIntro>
        {t(
          'entity.newVehicle.catalogue.intro.trims',
          'Trim levels are the equipment grades customers choose from. Each one needs a variant for every engine it is sold with.'
        )}
      </TabIntro>
      <Card
        title={t('entity.newVehicle.catalogue.trimLevels', 'Trim levels')}
        actions={
          canWrite
            ? [
                {
                  type: 'button',
                  title: t('entity.newVehicle.catalogue.addTrim', 'Add trim'),
                  leftIcon: 'content/add',
                  onClick: () => setCreateOpen(true),
                },
              ]
            : []
        }
      >
        <DataStatus
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={!rows.length}
          emptyMessage={t('entity.newVehicle.authoring.noTrims', 'No trim levels yet')}
          emptySubheadline={t(
            'entity.newVehicle.catalogue.empty.trimsHint',
            'Start with the grade most customers buy, then add the others.'
          )}
          action={
            canWrite
              ? {
                  title: t('entity.newVehicle.catalogue.addTrim', 'Add trim'),
                  leftIcon: 'content/add',
                  onClick: () => setCreateOpen(true),
                }
              : undefined
          }
        >
          <Table
            columns={[
              column(t('entity.newVehicle.catalogue.trimLevel', 'Trim level')),
              column(t('entity.newVehicle.catalogue.publication', 'Publication'), {width: '14%'}),
              column(t('entity.newVehicle.catalogue.orderability', 'Orderability'), {width: '22%'}),
              column(t('entity.newVehicle.catalogue.basePriceNet', 'Base price net'), {
                width: '14%',
                align: 'right',
              }),
              ...(canViewCost
                ? [
                    column(t('entity.newVehicle.catalogue.costNet', 'Cost net'), {
                      width: '14%',
                      align: 'right',
                    }),
                  ]
                : []),
              actionsColumn(),
            ]}
          >
            {rows.map((row) => (
              <TableRow key={row.id} onRowClick={canWrite ? () => editTrim(row) : undefined}>
                <Cell>
                  <HStack spacing={3} align="center">
                    <CatalogueThumbnail
                      image={row.imageAssetRef}
                      reserveSlot={showImage}
                      onImageError={refetchOnImageExpiry(query)}
                    />
                    <VStack spacing={0}>
                      <Text size="small" alternative>
                        {row.name}
                      </Text>
                      <Text size="xSmall" color="secondary">
                        {[
                          getBodyStyle(row.bodyStyle),
                          row.type && row.type !== row.name ? row.type : undefined,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Text>
                    </VStack>
                  </HStack>
                </Cell>
                <Cell>
                  <NewVehicleStatusFlag group="publicationStatus" value={row.publicationStatus} />
                </Cell>
                <Cell>
                  <OrderabilityChip trim={row} />
                </Cell>
                <Cell align="right">
                  <PriceWithGross amount={row.basePriceNet} currency={currency} vatRate={vatRate} />
                </Cell>
                {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
                {canViewCost ? (
                  <Cell align="right">
                    <MoneyText amount={row.costNet ?? '0.00'} currency={currency} />
                  </Cell>
                ) : null}
                <Cell>
                  <Show when={canWrite}>
                    <RowActions
                      actions={[
                        editRowAction(() => editTrim(row)),
                        archiveRowAction(() => archive(row)),
                      ]}
                    />
                  </Show>
                </Cell>
              </TableRow>
            ))}
          </Table>
        </DataStatus>
        <Dialog
          isOpen={isCreateOpen}
          title={t('entity.newVehicle.catalogue.addTrim', 'Add trim')}
          onClose={() => setCreateOpen(false)}
        >
          <Form<TrimValues>
            experimentalZodSchema={trimAuthoringSchema}
            defaultValues={{
              name: '',
              bodyStyle: '',
              type: '',
              publicationStatus: 'draft',
              commercialAvailability: 'preview',
              validFrom: null,
              validTo: null,
              basePriceNet: '0.00',
              costNet: '',
              copyFromTrimId: '',
            }}
            onSubmit={submit}
          >
            {(control) => (
              <TrimFields
                control={control}
                modelYearId={modelYearId}
                canViewCost={canViewCost}
                currency={currency}
                isCreate
                copyFromTrims={rows}
                submitTitle={t('entity.newVehicle.catalogue.addTrim', 'Add trim')}
              />
            )}
          </Form>
        </Dialog>
        <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
      </Card>
    </VStack>
  );
}

/**
 * Tells what was copied and how the trim was left: as chosen (`online`), Draft, or Draft with its
 * publish in the open group, which puts it online on Confirm.
 */
const reportCopy = (
  source: string,
  notCopied: string[],
  publication: 'online' | 'draft' | 'grouped'
) =>
  match({isPartial: notCopied.length > 0, publication})
    .with({isPartial: true, publication: 'grouped'}, () =>
      showNotification.warning(
        t(
          'entity.newVehicle.catalogue.copyFromTrimPartialGrouped',
          'The trim was created as Draft, and these were not copied from {trim}: {items}. Add them on their tabs. The trim goes online when you confirm the price list change group.',
          {trim: source, items: notCopied.join(', ')}
        )
      )
    )
    .with({isPartial: false, publication: 'grouped'}, () =>
      showNotification.success(
        t(
          'entity.newVehicle.catalogue.copyFromTrimGrouped',
          'The trim was created as Draft with the equipment, colours and packages of {trim}. It goes online when you confirm the price list change group.',
          {trim: source}
        )
      )
    )
    .with({isPartial: true, publication: 'online'}, () =>
      showNotification.warning(
        t(
          'entity.newVehicle.catalogue.copyFromTrimPartial',
          'The trim was created, but these were not copied from {trim}: {items}. Add them on their tabs.',
          {trim: source, items: notCopied.join(', ')}
        )
      )
    )
    .with({isPartial: true, publication: 'draft'}, () =>
      showNotification.warning(
        t(
          'entity.newVehicle.catalogue.copyFromTrimPartialDraft',
          'The trim was created as Draft, and these were not copied from {trim}: {items}. Add them on their tabs, then publish the trim.',
          {trim: source, items: notCopied.join(', ')}
        )
      )
    )
    .with({isPartial: false, publication: 'online'}, () =>
      showNotification.success(
        t(
          'entity.newVehicle.catalogue.copyFromTrimDone',
          'The trim was created with the equipment, colours and packages of {trim}.',
          {trim: source}
        )
      )
    )
    .with({isPartial: false, publication: 'draft'}, () =>
      showNotification.warning(
        t(
          'entity.newVehicle.catalogue.copyFromTrimDraft',
          'The trim was created as Draft with the equipment, colours and packages of {trim}. Publish it when it is ready.',
          {trim: source}
        )
      )
    )
    .exhaustive();

function EngineSubLine(props: {engine: EngineResponseBody}) {
  const subLine = [
    props.engine.factoryCode,
    props.engine.displacementCcm ? `${props.engine.displacementCcm} cm³` : undefined,
    formatBatteryCapacity(props.engine.technicalData),
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Show when={subLine !== ''}>
      <Text size="xSmall" color="secondary">
        {subLine}
      </Text>
    </Show>
  );
}

export function Engines({modelYearId, canWrite}: {modelYearId: string; canWrite: boolean}) {
  const query = useListEngineQuery({});
  const [createEngine] = useCreateEngineMutation();
  const [archiveEngine] = useArchiveEngineMutation();
  const [isCreateOpen, setCreateOpen] = useState(false);
  const labels = useNewVehicleCatalogueLabels();
  const rows =
    query.data?.items.filter((row) => row.modelYearId === modelYearId && !row.archived) ?? [];
  const submit: FormSubmitHandler<EngineValues> = (values, setErrors) =>
    createEngine({
      engineWriteRequestBody: {
        modelYearId,
        name: values.name,
        codes: [],
        fuel: values.fuel,
        hybridType: engineHybridType(values),
        powerKw: values.powerKw,
        powerPs: values.powerPs || null,
        displacementCcm: values.displacementCcm ? +values.displacementCcm : null,
        factoryCode: values.factoryCode || null,
        transmissionCode: null,
        transmission: values.transmission,
        drive: values.drive,
        technicalData: toEngineTechnicalData(values.technicalData, values),
        version: 1,
      },
    })
      .unwrap()
      .then(() => setCreateOpen(false))
      .catch((error: ApiError) => handleCatalogueError(error, setErrors));
  const editEngine = (row: EngineResponseBody) => {
    const dialogId = `engine-edit-${row.id}-${getRandomId()}`;
    openDialog(
      <EngineEditDialog
        engine={row}
        dialogId={dialogId}
        reload={async () => {
          await query.refetch();
        }}
      />,
      {
        id: dialogId,
        title: t('entity.newVehicle.catalogue.editEngineNamed', 'Edit engine · {name}', {
          name: row.name,
        }),
      }
    );
  };
  const archive = (row: EngineResponseBody) =>
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archiveEngine', 'Archive engine {name}?', {
        name: row.name,
      }),
      onConfirm: () =>
        void archiveEngine({engineId: row.id, versionedWriteRequestBody: {version: row.version}})
          .unwrap()
          .catch(handleCatalogueError),
    });
  return (
    <VStack spacing={3}>
      <TabIntro>
        {t(
          'entity.newVehicle.catalogue.intro.engines',
          'Engines are the powertrains this model year is sold with. Price each trim and engine combination in the Variant matrix.'
        )}
      </TabIntro>
      <Card
        title={t('entity.newVehicle.catalogue.engines', 'Engines')}
        actions={
          canWrite
            ? [
                {
                  type: 'button',
                  title: t('entity.newVehicle.catalogue.addEngine', 'Add engine'),
                  leftIcon: 'content/add',
                  onClick: () => setCreateOpen(true),
                },
              ]
            : []
        }
      >
        <DataStatus
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={!rows.length}
          emptyMessage={t('entity.newVehicle.authoring.noEngines', 'No engines yet')}
          emptySubheadline={t(
            'entity.newVehicle.catalogue.empty.enginesHint',
            'Add every powertrain this model year can be ordered with.'
          )}
          action={
            canWrite
              ? {
                  title: t('entity.newVehicle.catalogue.addEngine', 'Add engine'),
                  leftIcon: 'content/add',
                  onClick: () => setCreateOpen(true),
                }
              : undefined
          }
        >
          <Table
            columns={[
              column(t('entity.newVehicle.catalogue.engine', 'Engine')),
              column(t('entity.newVehicle.catalogue.fuel', 'Fuel'), {width: '14%'}),
              column(t('entity.newVehicle.catalogue.power', 'Power'), {
                width: '14%',
                align: 'right',
              }),
              column(t('entity.newVehicle.catalogue.transmission', 'Transmission'), {width: '14%'}),
              column(t('entity.newVehicle.catalogue.drive', 'Drive'), {width: '16%'}),
              actionsColumn(),
            ]}
          >
            {rows.map((row) => (
              <TableRow key={row.id} onRowClick={canWrite ? () => editEngine(row) : undefined}>
                <Cell>
                  <VStack spacing={0}>
                    <Text size="small" alternative>
                      {row.name}
                    </Text>
                    <EngineSubLine engine={row} />
                  </VStack>
                </Cell>
                <Cell>{labels.formatFuel(row)}</Cell>
                <Cell align="right">
                  {row.powerPs
                    ? `${Number(row.powerKw)} kW · ${Number(row.powerPs)} PS`
                    : `${Number(row.powerKw)} kW`}
                </Cell>
                <Cell>{labels.getTransmission(row.transmission)}</Cell>
                <Cell>{labels.getDrive(row.drive)}</Cell>
                <Cell>
                  <Show when={canWrite}>
                    <RowActions
                      actions={[
                        editRowAction(() => editEngine(row)),
                        archiveRowAction(() => archive(row)),
                      ]}
                    />
                  </Show>
                </Cell>
              </TableRow>
            ))}
          </Table>
        </DataStatus>
        <Dialog
          isOpen={isCreateOpen}
          title={t('entity.newVehicle.catalogue.addEngine', 'Add engine')}
          onClose={() => setCreateOpen(false)}
        >
          <Form<EngineValues>
            experimentalZodSchema={engineAuthoringSchema}
            defaultValues={{
              name: '',
              fuel: '',
              hybridType: '',
              powerKw: '',
              powerPs: '',
              displacementCcm: '',
              transmission: '',
              drive: '',
              factoryCode: '',
              technicalData: toEngineTechnicalDataValues(undefined),
            }}
            onSubmit={submit}
          >
            {(control) => (
              <EngineFields
                control={control}
                isCreate
                submitTitle={t('entity.newVehicle.catalogue.addEngine', 'Add engine')}
              />
            )}
          </Form>
        </Dialog>
      </Card>
    </VStack>
  );
}
